import api from './api';
import { Angebot, AngebotCreate, AngebotList, Projekt } from '../types';

interface AngebotListParams {
  kunde_id?: number;
  projekt_id?: number;
  status?: string;
}

const downloadBlob = (data: Blob, contentDisposition: string | undefined, fallbackName: string) => {
  const filename = contentDisposition?.match(/filename="?(.+?)"?$/)?.[1] || fallbackName;
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const readBlobErrorDetail = async (error: unknown): Promise<string | null> => {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const parsed = JSON.parse(await data.text());
    return typeof parsed?.detail === 'string' ? parsed.detail : null;
  } catch {
    return null;
  }
};

const angeboteService = {
  list: async (params?: AngebotListParams): Promise<AngebotList[]> => {
    const response = await api.get('/angebote/', { params });
    return response.data;
  },

  get: async (id: number): Promise<Angebot> => {
    const response = await api.get(`/angebote/${id}`);
    return response.data;
  },

  create: async (data: AngebotCreate): Promise<Angebot> => {
    const response = await api.post('/angebote/', data);
    return response.data;
  },

  update: async (id: number, data: Partial<AngebotCreate>): Promise<Angebot> => {
    const response = await api.put(`/angebote/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/angebote/${id}`);
  },

  updateStatus: async (id: number, status: string, ablehnungsgrund?: string): Promise<Angebot> => {
    const response = await api.post(`/angebote/${id}/status`, { status, ablehnungsgrund });
    return response.data;
  },

  getNextNumber: async (): Promise<string> => {
    const response = await api.get('/angebote/naechste-nummer');
    return response.data.angebotsnummer;
  },

  generateDocx: async (id: number): Promise<void> => {
    try {
      const response = await api.get(`/angebote/${id}/docx`, {
        responseType: 'blob',
        params: { v: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      downloadBlob(response.data, response.headers['content-disposition'], `angebot-${id}.docx`);
    } catch (error) {
      const detail = await readBlobErrorDetail(error);
      throw detail ? new Error(detail) : error;
    }
  },

  generatePdf: async (id: number): Promise<void> => {
    try {
      const response = await api.get(`/angebote/${id}/pdf`, {
        responseType: 'blob',
        params: { v: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      downloadBlob(response.data, response.headers['content-disposition'], `angebot-${id}.pdf`);
    } catch (error) {
      const detail = await readBlobErrorDetail(error);
      throw detail ? new Error(detail) : error;
    }
  },

  createProjekt: async (id: number, alsGewonnen = true): Promise<Projekt> => {
    const response = await api.post(`/angebote/${id}/projekt-erzeugen`, { als_gewonnen: alsGewonnen });
    return response.data;
  },
};

export default angeboteService;
